// PSR sections for the Overview VoltaGrid tab — 2 Week Look Ahead, Top Risks,
// Top Issues, Impact Log, Top Accomplishments and Observations. Lifted from the project
// status report workbook (its Indexes and Cash Out boxes are left out on
// purpose — the VoltaGrid jobs carry no productivity tracker or phase-hours
// plan, so there is no PF/SPI to trend; the look ahead is a written one).
//
// Everything here is PM-entered and saved as 'Overview' rows on the selected
// version, next to the rest of the page's rows:
//   VgLookAhead        rowId '1'
//   VgRisk / VgIssue   rowId = record id, value = JSON ('' once deleted)
//   VgAccomplishments  rowId '1'
//   VgImpact           rowId = record id, value = JSON ('' once deleted)
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';

const LOOK_AHEAD_WEEKS = 2;
const LEVELS = ['High', 'Medium', 'Low'];
// Forecast_Value is nvarchar(1024) and the save path truncates to fit; a cut
// JSON string won't parse back, so the free-text fields are capped to leave
// the record room.
const TEXT_MAX = 420;
const NOTES_MAX = 1000;

// Risks and issues share one table. A row's `_kind` ('risk' | 'issue') picks
// which field the right-hand column reads: Impact for a risk, Priority for an
// issue. Storage is unchanged (VgRisk / VgIssue rows), so nothing already
// entered moves. The Probability / Resolution column came off 10/8 (Seth);
// probability and resolutionDate stay on the stored rows, just not shown.
const KINDS = [{ key: 'risk', label: 'Risk' }, { key: 'issue', label: 'Issue' }];
// No Type column: the kind still rides on the row (it picks the rating field
// and the storage row) but isn't shown or edited.
const RECORD_COLUMNS = [
    { key: 'description', label: 'Description', type: 'text', width: '42%' },
    { key: 'action', label: 'Action Taken', type: 'text', width: '38%' },
    { key: 'rating', label: 'Impact / Priority', resolve: (rec) => (rec._kind === 'issue' ? { key: 'priority', type: 'level' } : { key: 'impact', type: 'level' }) },
];

// Impact log: events that moved the job's schedule or cost — what happened,
// who caused it, the days and dollars, and where it stands (CO / ref number
// when one is raised). Running log, so it reads oldest first and totals the
// days and dollars at the foot.
const IMPACT_CAUSES = ['Client', 'Weather', 'Vendor / Supplier', 'Design', 'Site Conditions', 'Strike', 'Other'];
const IMPACT_STATUSES = ['Open', 'Submitted', 'Approved', 'Rejected', 'Closed'];
const IMPACT_COLUMNS = [
    { key: 'date', label: 'Date', type: 'date', width: '9%' },
    { key: 'description', label: 'Description', type: 'text', width: '36%' },
    { key: 'cause', label: 'Cause', type: 'select', options: IMPACT_CAUSES, width: '12%' },
    { key: 'ref', label: 'CO / Ref #', type: 'short', width: '9%' },
    { key: 'days', label: 'Schedule (Days)', type: 'number', total: true, width: '10%' },
    { key: 'cost', label: 'Cost ($)', type: 'number', money: true, total: true, width: '11%' },
    { key: 'status', label: 'Status', type: 'status', options: IMPACT_STATUSES, width: '10%' },
];
const SHORT_MAX = 40;

const statusColors = (palette, status) => {
    if (status === 'Approved') return { color: palette.green, background: palette.greenLight };
    if (status === 'Rejected') return { color: palette.red, background: palette.redLight };
    if (status === 'Open') return { color: palette.orange, background: palette.orangeLight };
    if (status === 'Submitted') return { color: palette.blue700, background: palette.blue50 };
    return { color: palette.muted, background: '#f1f5f9' };
};

const toNum = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const fmtNum = (n, money) => (money
    ? `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
    : n.toLocaleString('en-US', { maximumFractionDigits: 1 }));

// Saves don't write back to the shared user-data atom, so leaving the tab and
// coming back would re-read rows from before the edit. Edits made this session
// are kept here and laid over the loaded rows.
const sessionEdits = {};
const editKey = (job, version, fieldName, rowId) => `${job}|${version}|${fieldName}|${rowId}`;

const levelColors = (palette, level) => {
    if (level === 'High') return { color: palette.red, background: palette.redLight };
    if (level === 'Medium') return { color: palette.orange, background: palette.orangeLight };
    if (level === 'Low') return { color: palette.green, background: palette.greenLight };
    return { color: palette.muted, background: 'transparent' };
};

// `badge` sits beside the title (the look-ahead week tag); `action` stays on
// the right (the Add buttons).
function Section({ palette, title, badge, action, children }) {
    return (
        <div className="vg-psr-section" style={{ background: palette.card, borderRadius: 10, border: `1px solid ${palette.border}`, boxShadow: '0 4px 16px rgba(15,23,42,.06)', padding: 16, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h3 style={{ margin: 0, fontSize: 11, fontWeight: 700, color: palette.text, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>{title}</h3>
                    {badge}
                </div>
                {action}
            </div>
            {children}
        </div>
    );
}

// Click-to-edit free text box (look ahead, accomplishments). `draft` is null
// while displaying and the working copy while editing.
function NotesBox({ palette, value, draft, setDraft, locked, placeholder, onSave }) {
    if (draft !== null) {
        return (
            <div>
                <textarea value={draft} maxLength={NOTES_MAX} autoFocus spellCheck onChange={e => setDraft(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', minHeight: 80, padding: 10, fontSize: 12, border: `1px solid ${palette.blue300}`, borderRadius: 8, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6, outline: 'none', background: palette.blue50 }}
                    placeholder={placeholder} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                    <button onClick={() => { onSave(draft); setDraft(null); }}
                        style={{ padding: '5px 14px', background: `linear-gradient(135deg, ${palette.blue500}, ${palette.blue700})`, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 11 }}>Save</button>
                    <button onClick={() => setDraft(null)}
                        style={{ padding: '5px 14px', background: palette.blue50, color: palette.text, border: `1px solid ${palette.border}`, borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>Cancel</button>
                    <span style={{ marginLeft: 'auto', fontSize: 9, color: palette.muted }}>{draft.length} / {NOTES_MAX}</span>
                </div>
            </div>
        );
    }
    return (
        <div onClick={() => { if (!locked) setDraft(value); }}
            style={{ cursor: locked ? 'default' : 'pointer', padding: 10, borderRadius: 8, border: `1px dashed ${palette.border}`, minHeight: 40, fontSize: 12, color: value ? palette.text : palette.blue300, fontStyle: value ? 'normal' : 'italic', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {value || (locked ? 'None entered.' : placeholder)}
        </div>
    );
}

const alignOf = (col) => (col.type === 'text' || col.type === 'short' ? 'left' : col.type === 'number' ? 'right' : 'center');

function RecordTable({ palette, columns, records, locked, confirmText, onSave, onDelete, editingId, setEditingId, onCancelNew }) {
    const [draft, setDraft] = useState(null);
    // Seeded only when a different row opens — `records` changing under an
    // open editor must not wipe what's been typed.
    useEffect(() => {
        setDraft(editingId ? { ...(records.find(r => r.id === editingId) || {}) } : null);
    }, [editingId]);

    const th = { padding: '5px 6px', fontSize: 8, fontWeight: 700, color: palette.muted, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `2px solid ${palette.border}`, whiteSpace: 'nowrap' };
    const td = { padding: '5px 6px', fontSize: 10, borderBottom: `1px solid ${palette.border}`, verticalAlign: 'top' };
    const input = { width: '100%', boxSizing: 'border-box', padding: '3px 5px', fontSize: 10, fontFamily: 'inherit', border: `1px solid ${palette.blue300}`, borderRadius: 5, outline: 'none' };

    const editor = (col0) => {
        const col = col0.resolve ? { ...col0, ...col0.resolve(draft || {}) } : col0;
        const set = (v) => setDraft(d => ({ ...d, [col.key]: v }));
        const v = draft?.[col.key] || '';
        if (col.type === 'kind') {
            return (
                <select value={v || 'risk'} onChange={e => set(e.target.value)} style={input}>
                    {KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select>
            );
        }
        if (col.type === 'level') {
            return (
                <select value={v} onChange={e => set(e.target.value)} style={input}>
                    <option value=""></option>
                    {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
            );
        }
        if (col.type === 'select' || col.type === 'status') {
            return (
                <select value={v} onChange={e => set(e.target.value)} style={input}>
                    <option value=""></option>
                    {col.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
            );
        }
        if (col.type === 'date') return <input type="date" value={v} onChange={e => set(e.target.value)} style={input} />;
        if (col.type === 'number') return <input type="number" step="any" value={v} onChange={e => set(e.target.value)} style={{ ...input, textAlign: 'right' }} />;
        if (col.type === 'short') return <input type="text" value={v} maxLength={SHORT_MAX} spellCheck={false} onChange={e => set(e.target.value)} style={input} />;
        return <textarea value={v} maxLength={TEXT_MAX} spellCheck onChange={e => set(e.target.value)} autoFocus={col.key === 'description'} style={{ ...input, minHeight: 40, resize: 'vertical', lineHeight: 1.4 }} />;
    };

    const display = (col0, rec) => {
        const col = col0.resolve ? { ...col0, ...col0.resolve(rec) } : col0;
        const v = rec[col.key] || '';
        if (col.type === 'kind') {
            const issue = v === 'issue';
            return <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 999, fontSize: 9, fontWeight: 700, color: issue ? palette.blue700 : palette.text, background: issue ? palette.blue50 : '#f1f5f9' }}>{issue ? 'Issue' : 'Risk'}</span>;
        }
        if (col.type === 'level') {
            if (!v) return '';
            return <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 999, fontSize: 9, fontWeight: 700, ...levelColors(palette, v) }}>{v}</span>;
        }
        if (col.type === 'status') {
            if (!v) return '';
            return <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 999, fontSize: 9, fontWeight: 700, ...statusColors(palette, v) }}>{v}</span>;
        }
        if (col.type === 'date') return v ? moment(v).format('DD-MMM-YY') : '';
        if (col.type === 'number') return v === '' ? '' : fmtNum(toNum(v), col.money);
        if (col.type === 'select' || col.type === 'short') return v;
        return <span style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{v}</span>;
    };

    if (records.length === 0) {
        return <div style={{ fontSize: 11, color: palette.muted, fontStyle: 'italic', padding: '2px 0' }}>None entered.</div>;
    }

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 10 }}>
                <thead>
                    <tr>
                        {columns.map(col => (
                            <th key={col.key} style={{ ...th, width: col.width, textAlign: alignOf(col) }}>{col.label}</th>
                        ))}
                        {!locked && <th className="no-print" style={th} />}
                    </tr>
                </thead>
                <tbody>
                    {records.map(rec => {
                        const editing = editingId === rec.id && draft;
                        return (
                            <tr key={rec.id}>
                                {columns.map(col => (
                                    <td key={col.key} style={{ ...td, textAlign: alignOf(col), fontVariantNumeric: col.type === 'number' ? 'tabular-nums' : undefined, fontWeight: col.key === 'description' && !editing ? 600 : 400 }}>
                                        {editing ? editor(col) : display(col, rec)}
                                    </td>
                                ))}
                                {!locked && (
                                    <td className="no-print" style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                                        {editing ? (
                                            <>
                                                <button onClick={() => { onSave(draft); setEditingId(null); }}
                                                    style={{ marginRight: 3, padding: '3px 8px', fontSize: 9, cursor: 'pointer', background: palette.blue600, color: '#fff', border: 'none', borderRadius: 5, fontWeight: 600 }}>Save</button>
                                                <button onClick={() => { onCancelNew(rec.id); setEditingId(null); }}
                                                    style={{ padding: '3px 8px', fontSize: 9, cursor: 'pointer', background: palette.blue100, color: palette.blue700, border: 'none', borderRadius: 5, fontWeight: 600 }}>Cancel</button>
                                            </>
                                        ) : (
                                            <>
                                                <button onClick={() => setEditingId(rec.id)} title="Edit"
                                                    style={{ marginRight: 3, padding: '2px 5px', fontSize: 10, cursor: 'pointer', background: 'transparent', color: palette.text, border: `1px solid ${palette.border}`, borderRadius: 5, display: 'inline-flex', alignItems: 'center' }}>
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                                </button>
                                                <button onClick={() => { if (window.confirm(confirmText)) onDelete(rec.id); }} title="Delete"
                                                    style={{ padding: '2px 5px', fontSize: 10, cursor: 'pointer', background: 'transparent', color: palette.muted, border: `1px solid ${palette.border}`, borderRadius: 5 }}>✕</button>
                                            </>
                                        )}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
                {columns.some(c => c.total) && (
                    <tfoot>
                        <tr>
                            {columns.map((col, i) => (
                                <td key={col.key} style={{ ...td, borderBottom: 'none', borderTop: `2px solid ${palette.border}`, fontWeight: 700, textAlign: alignOf(col), fontVariantNumeric: 'tabular-nums' }}>
                                    {col.total ? fmtNum(records.reduce((sum, r) => sum + toNum(r[col.key]), 0), col.money) : (i === 0 ? 'Total' : '')}
                                </td>
                            ))}
                            {!locked && <td className="no-print" style={{ ...td, borderBottom: 'none', borderTop: `2px solid ${palette.border}` }} />}
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}

function VoltaGridPsrSections({ palette, jobUserData, selectedJob, selectedVersion, versionInfo, locked, save }) {
    const [lookAhead, setLookAhead] = useState('');
    const [lookAheadDraft, setLookAheadDraft] = useState(null);
    const [risks, setRisks] = useState([]);
    const [issues, setIssues] = useState([]);
    const [accomplishments, setAccomplishments] = useState('');
    const [accomplishmentsDraft, setAccomplishmentsDraft] = useState(null);
    const [editingRecordId, setEditingRecordId] = useState(null);
    const [impacts, setImpacts] = useState([]);
    const [editingImpactId, setEditingImpactId] = useState(null);

    /* ─────────────── LOAD SAVED ROWS ─────────────── */
    useEffect(() => {
        const values = {};
        (jobUserData || []).forEach(item => {
            if (item.Forecast_Tab !== 'Overview' || item.Version_GUID !== selectedVersion || item.BU_ID !== selectedJob) return;
            if (!String(item.Forecast_Field_Name || '').startsWith('Vg')) return;
            values[`${item.Forecast_Field_Name}|${item.Forecast_Row_ID}`] = item.Forecast_Value || '';
        });
        const prefix = `${selectedJob}|${selectedVersion}|`;
        Object.keys(sessionEdits).forEach(k => { if (k.startsWith(prefix)) values[k.slice(prefix.length)] = sessionEdits[k]; });

        const loadedRisks = [], loadedIssues = [], loadedImpacts = [];
        let notes = '', ahead = '';
        Object.entries(values).forEach(([k, value]) => {
            const cut = k.indexOf('|');
            const fieldName = k.slice(0, cut), rowId = k.slice(cut + 1);
            if (fieldName === 'VgLookAhead') { ahead = value; return; }
            if (fieldName === 'VgAccomplishments') { notes = value; return; }
            if (fieldName === 'VgImpact') {
                try { loadedImpacts.push({ ...JSON.parse(value), id: rowId }); } catch (e) { }
                return;
            }
            if (fieldName !== 'VgRisk' && fieldName !== 'VgIssue') return;
            // '' marks a deleted record.
            try { (fieldName === 'VgRisk' ? loadedRisks : loadedIssues).push({ ...JSON.parse(value), id: rowId }); } catch (e) { }
        });
        const byId = (a, b) => String(a.id).localeCompare(String(b.id));
        setRisks(loadedRisks.sort(byId));
        setIssues(loadedIssues.sort(byId));
        setImpacts(loadedImpacts);
        setLookAhead(ahead);
        setLookAheadDraft(null);
        setAccomplishments(notes);
        setAccomplishmentsDraft(null);
        setEditingRecordId(null);
        setEditingImpactId(null);
    }, [jobUserData, selectedJob, selectedVersion]);

    const persist = (fieldName, value, rowId = '1') => {
        sessionEdits[editKey(selectedJob, selectedVersion, fieldName, rowId)] = value;
        save(fieldName, value, rowId);
    };

    /* ─────────────── LOOK AHEAD WEEKS ─────────────── */
    // Anchored on the version's start like the crew look ahead: the report cut
    // on a Monday looks ahead at the week it opens plus the next one.
    const lookAheadLabel = useMemo(() => {
        const anchor = versionInfo?.Version_Start_DateTime ? moment(versionInfo.Version_Start_DateTime) : moment();
        const w0 = anchor.clone().startOf('isoWeek');
        const ends = [];
        for (let i = 0; i < LOOK_AHEAD_WEEKS; i++) ends.push(w0.clone().add(i, 'weeks').add(6, 'days').format('M/D'));
        return `WE ${ends.join(' & ')}`;
    }, [versionInfo]);

    /* ─────────────── RISK / ISSUE RECORDS ─────────────── */
    // One table over both lists. Each row carries `_kind`; a save routes it to
    // the VgRisk or VgIssue row, and a row whose kind was changed while editing
    // is deleted from the list it came from.
    const records = useMemo(() => [
        ...risks.map(r => ({ ...r, _kind: 'risk' })),
        ...issues.map(r => ({ ...r, _kind: 'issue' })),
    ].sort((a, b) => String(a.id).localeCompare(String(b.id))), [risks, issues]);
    const listOf = (kind) => (kind === 'issue' ? [issues, setIssues, 'VgIssue'] : [risks, setRisks, 'VgRisk']);
    const recordOps = {
        add: () => {
            const rec = { id: Date.now().toString(), _new: true };
            setRisks([...risks, rec]);
            setEditingRecordId(rec.id);
        },
        save: (draft) => {
            const { _new, _kind, ...rec } = draft;
            const kind = _kind === 'issue' ? 'issue' : 'risk';
            const was = issues.find(r => r.id === rec.id) ? 'issue' : 'risk';
            const [list, setList, fieldName] = listOf(kind);
            if (was !== kind) {
                const [oldList, setOldList, oldField] = listOf(was);
                setOldList(oldList.filter(r => r.id !== rec.id));
                if (!_new) persist(oldField, '', rec.id);
                setList([...list, rec]);
            } else {
                setList(list.map(r => r.id === rec.id ? rec : r));
            }
            const { id, ...body } = rec;
            persist(fieldName, JSON.stringify(body), id);
        },
        remove: (id) => {
            const kind = issues.find(r => r.id === id) ? 'issue' : 'risk';
            const [list, setList, fieldName] = listOf(kind);
            setList(list.filter(r => r.id !== id));
            persist(fieldName, '', id);
        },
        // Cancelling a row that was never saved drops it.
        cancelNew: (id) => {
            if (risks.find(r => r.id === id)?._new) setRisks(risks.filter(r => r.id !== id));
            if (issues.find(r => r.id === id)?._new) setIssues(issues.filter(r => r.id !== id));
        },
    };

    /* ─────────────── IMPACT LOG ─────────────── */
    // Oldest first by date; undated rows (a new one being typed) go last.
    const impactRows = useMemo(() => [...impacts].sort((a, b) =>
        (a.date ? 0 : 1) - (b.date ? 0 : 1) || String(a.date || '').localeCompare(String(b.date || '')) || String(a.id).localeCompare(String(b.id))
    ), [impacts]);
    const impactOps = {
        add: () => {
            const rec = { id: Date.now().toString(), _new: true, date: moment().format('YYYY-MM-DD'), status: 'Open' };
            setImpacts([...impacts, rec]);
            setEditingImpactId(rec.id);
        },
        save: (draft) => {
            const { _new, ...rec } = draft;
            setImpacts(impacts.map(r => r.id === rec.id ? rec : r));
            const { id, ...body } = rec;
            persist('VgImpact', JSON.stringify(body), id);
        },
        remove: (id) => {
            setImpacts(impacts.filter(r => r.id !== id));
            persist('VgImpact', '', id);
        },
        cancelNew: (id) => {
            if (impacts.find(r => r.id === id)?._new) setImpacts(impacts.filter(r => r.id !== id));
        },
    };

    const addButton = (onClick) => locked ? null : (
        <button onClick={onClick} className="no-print"
            style={{ padding: '3px 10px', fontSize: 9, cursor: 'pointer', background: 'transparent', color: palette.muted, border: `1px solid ${palette.border}`, borderRadius: 5, fontWeight: 600, letterSpacing: '.02em' }}>
            + Add
        </button>
    );

    return (
        <>
            <style>{`
                @media print {
                    .vg-psr-section { page-break-inside: avoid !important; box-shadow: none !important; }
                }
            `}</style>

            {/* ═══════════ 2 WEEK LOOK AHEAD ═══════════ */}
            <Section palette={palette} title="2 Week Look Ahead"
                badge={<span style={{ fontSize: 9, fontWeight: 600, color: palette.blue700, background: palette.blue50, padding: '2px 8px', borderRadius: 999 }}>{lookAheadLabel}</span>}>
                <NotesBox palette={palette} value={lookAhead} draft={lookAheadDraft} setDraft={setLookAheadDraft} locked={locked}
                    placeholder="What's coming up over the next two weeks..."
                    onSave={(v) => { setLookAhead(v); persist('VgLookAhead', v); }} />
            </Section>

            {/* ═══════════ TOP RISKS & ISSUES ═══════════ */}
            <Section palette={palette} title="Top Risks & Issues" action={addButton(recordOps.add)}>
                <RecordTable palette={palette} columns={RECORD_COLUMNS} records={records} locked={locked} confirmText="Delete this row?"
                    onSave={recordOps.save} onDelete={recordOps.remove} onCancelNew={recordOps.cancelNew}
                    editingId={editingRecordId} setEditingId={setEditingRecordId} />
            </Section>

            {/* ═══════════ IMPACT LOG ═══════════ */}
            <Section palette={palette} title="Impact Log" action={addButton(impactOps.add)}>
                <RecordTable palette={palette} columns={IMPACT_COLUMNS} records={impactRows} locked={locked} confirmText="Delete this impact?"
                    onSave={impactOps.save} onDelete={impactOps.remove} onCancelNew={impactOps.cancelNew}
                    editingId={editingImpactId} setEditingId={setEditingImpactId} />
            </Section>

            {/* ═══════════ TOP ACCOMPLISHMENTS AND OBSERVATIONS ═══════════ */}
            <Section palette={palette} title="Top Accomplishments and Observations">
                <NotesBox palette={palette} value={accomplishments} draft={accomplishmentsDraft} setDraft={setAccomplishmentsDraft} locked={locked}
                    placeholder="One accomplishment or observation per line..."
                    onSave={(v) => { setAccomplishments(v); persist('VgAccomplishments', v); }} />
            </Section>
        </>
    );
}

export default VoltaGridPsrSections;
